import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Phone, Search, Check, Clock, User, Mail, MessageCircle } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactRequestsManagement = () => {
  const [requests, setRequests] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${API}/admin/contact-requests`);
      setRequests(response.data || []);
    } catch (error) {
      console.error('Error fetching contact requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResolve = async (requestId) => {
    try {
      await axios.put(`${API}/admin/contact-requests/${requestId}`, { status: 'Resolved' });
      toast({
        title: '✅ Success',
        description: 'Request marked as resolved',
      });
      fetchRequests();
    } catch (error) {
      toast({
        title: '❌ Error',
        description: 'Failed to update request',
        variant: 'destructive',
      });
    }
  };

  const filteredRequests = requests.filter(req => {
    const search = searchTerm.toLowerCase();
    return (
      req.userName?.toLowerCase().includes(search) ||
      req.userEmail?.toLowerCase().includes(search) ||
      req.userPhone?.includes(search) ||
      req.clientId?.toString().includes(search) ||
      req.requestId?.toLowerCase().includes(search)
    );
  });

  const pendingCount = requests.filter(r => r.status === 'Pending').length;

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="w-12 h-12 border-4 border-green-200 border-t-green-500 rounded-full animate-spin mx-auto mb-4" />
        <p className="text-slate-600">Loading contact requests...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-blue-600 flex items-center">
          <Phone className="w-6 h-6 mr-2 text-green-600" />
          Contact Requests
        </h2>
        <div className="flex items-center space-x-4">
          {pendingCount > 0 && (
            <span className="px-3 py-1 bg-yellow-500/20 text-yellow-400 rounded-full text-sm font-medium animate-pulse">
              {pendingCount} Pending
            </span>
          )}
          <span className="text-sm text-slate-500">Total: {requests.length}</span>
        </div>
      </div>

      {/* Search */}
      <Card className="bg-white shadow-lg border-slate-200">
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-3 w-4 h-4 text-slate-500" />
            <Input
              placeholder="Search by Name, Email, Phone, Client ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-white border-slate-200 text-slate-800 placeholder:text-slate-400"
            />
          </div>
        </CardContent>
      </Card>

      {/* Requests List */}
      <div className="grid gap-4">
        {filteredRequests.length === 0 ? (
          <Card className="bg-white shadow-lg border-slate-200">
            <CardContent className="py-12 text-center">
              <MessageCircle className="w-16 h-16 mx-auto mb-4 text-slate-600" />
              <p className="text-slate-600">No contact requests found</p>
            </CardContent>
          </Card>
        ) : (
          filteredRequests.map(request => (
            <Card 
              key={request.id || request.requestId} 
              className={`bg-white shadow-lg border-slate-200 hover:border-white/30 transition-all animate-in fade-in slide-in-from-bottom-2 ${
                request.status === 'Pending' ? 'border-l-4 border-l-yellow-500' : 'border-l-4 border-l-green-500'
              }`}
            >
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1 space-y-3">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center text-slate-800 font-bold">
                        {request.userName?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <div>
                        <p className="font-semibold text-slate-800 text-lg">{request.userName}</p>
                        <p className="text-sm text-blue-400">#{request.clientId}</p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        request.status === 'Pending' 
                          ? 'bg-yellow-500/20 text-yellow-400' 
                          : 'bg-green-100 text-green-600'
                      }`}>
                        {request.status === 'Pending' ? (
                          <span className="flex items-center"><Clock className="w-3 h-3 mr-1" /> Pending</span>
                        ) : (
                          <span className="flex items-center"><Check className="w-3 h-3 mr-1" /> Resolved</span>
                        )}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-xs text-slate-500 font-medium mb-1">Request ID</p>
                        <p className="text-blue-600 font-mono">{request.requestId}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 font-medium mb-1 flex items-center">
                          <Mail className="w-3 h-3 mr-1" /> Email
                        </p>
                        <p className="text-slate-800">{request.userEmail}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 font-medium mb-1 flex items-center">
                          <Phone className="w-3 h-3 mr-1" /> Phone
                        </p>
                        <p className="text-slate-800">{request.userPhone}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 font-medium mb-1">Submitted</p>
                        <p className="text-slate-800">{new Date(request.createdAt).toLocaleString()}</p>
                      </div>
                    </div>

                    {request.message && (
                      <div className="p-3 bg-white rounded-lg">
                        <p className="text-xs text-slate-500 font-medium mb-1">Message</p>
                        <p className="text-slate-600">{request.message}</p>
                      </div>
                    )}
                  </div>

                  {request.status === 'Pending' && (
                    <Button
                      onClick={() => handleResolve(request.requestId)}
                      className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 shadow-lg hover:shadow-green-500/50 transition-all hover:scale-105"
                    >
                      <Check className="w-4 h-4 mr-2" />
                      Mark Resolved
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default ContactRequestsManagement;
